import { useState, useEffect } from 'react';
import { TT } from '@features/ai-prompting/terminalTokens';

/**
 * The result, fixed order for every format and both outcomes (spec Part
 * 6): verdict strip, rubric feedback, deterministic checks, hidden cases.
 * Score is never the headline (Part 1.3) — PASSED/NOT YET is — and no
 * attempt-to-attempt delta is ever computed here: this component only
 * ever sees the CURRENT result, nothing from a prior attempt.
 */
const Chevron = ({ open }) => (
  <span style={{ display: 'inline-block', transition: 'transform 0.15s', transform: open ? 'rotate(90deg)' : 'none' }}>▸</span>
);

const CriterionRow = ({ name, passed, detail }) => (
  <div style={{ padding: '8px 0', borderBottom: `1px solid ${TT.border}` }}>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontFamily: TT.fontMono, fontSize: 12.5 }}>
      <span style={{ color: passed ? TT.green : '#ef4444' }}>{passed ? 'PASS' : 'MISS'}</span>
      <span style={{ color: TT.text, fontWeight: 600 }}>{name}</span>
    </div>
    {detail && <p style={{ fontFamily: TT.fontSans, fontSize: 12, color: TT.textMuted, lineHeight: 1.6, margin: '4px 0 0' }}>{detail}</p>}
  </div>
);

const CollapsibleSection = ({ title, defaultOpen, resetKey, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => setOpen(defaultOpen), [resetKey, defaultOpen]);
  return (
    <div style={{ marginTop: 16 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none',
          padding: 0, cursor: 'pointer', fontFamily: TT.fontMono, fontSize: 11, color: TT.textDim,
          textTransform: 'uppercase', letterSpacing: '0.05em',
        }}
      >
        <Chevron open={open} /> {title}
      </button>
      {open && <div style={{ marginTop: 8 }}>{children}</div>}
    </div>
  );
};

const HiddenCaseCard = ({ position, passed, capability }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      border: `1px solid ${passed ? TT.border : '#ef444455'}`, borderRadius: TT.radius.sm,
      padding: '10px 12px', marginBottom: 8,
    }}>
      <button
        onClick={() => !passed && setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
          background: 'transparent', border: 'none', padding: 0, cursor: passed ? 'default' : 'pointer',
          fontFamily: TT.fontMono, fontSize: 12.5,
        }}
      >
        <span style={{ color: TT.text }}>case {position}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: passed ? TT.green : '#ef4444', fontWeight: 700 }}>{passed ? 'PASSED' : 'FAILED'}</span>
          {!passed && <Chevron open={open} />}
        </span>
      </button>
      {open && capability && (
        <p style={{ fontFamily: TT.fontSans, fontSize: 12, color: TT.textMuted, lineHeight: 1.6, margin: '8px 0 0' }}>{capability}</p>
      )}
    </div>
  );
};

const interpretiveSentence = (passed, feedback, detChecks) => {
  if (passed) {
    const strongest = feedback.find(f => f.ok);
    return strongest?.text || 'Every criterion held up on this attempt.';
  }
  const firstMissed = feedback.find(f => !f.ok) ?? detChecks.find(c => !c.passed);
  if (!firstMissed) return 'Below the pass threshold — see the breakdown below.';
  return firstMissed.text || firstMissed.detail || `Start with: ${firstMissed.criterion || firstMissed.name}`;
};

// Sandbox part 4 item 2: result explanations are prose, not a work
// surface — capped at ~70ch so they stay readable even when the right
// column is wide, unlike EvaluationTrace (a work surface) beside it.
const PROSE_MAX_WIDTH = 640;

// A pass that was scored but not stored. Same text styles as the rest of the
// panel: the system-error line (mono 12, cyan — "not your fault", never styled
// like a fail) and the `$ continue` / `$ revise` action text.
const SaveNotice = ({ save }) => {
  if (!save) return null;
  const saving = save.status === 'saving';
  if (save.status === 'saved' && save.recovered) {
    return <div style={{ marginTop: 8, fontFamily: TT.fontMono, fontSize: 12, color: TT.green }}>✓ Saved.</div>;
  }
  if (save.status !== 'unsaved' && !saving) return null;
  return (
    <div role="status" style={{ marginTop: 8, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
      <span style={{ fontFamily: TT.fontMono, fontSize: 12, color: TT.cyan }}>
        ⚠ Passed — but we couldn't save it.{save.error ? ` ${save.error}` : ''}{!save.canRetry && ' Run the rep again to record it.'}
      </span>
      {save.canRetry && (
        <button
          onClick={save.retry}
          disabled={saving}
          className="ps-focusable"
          style={{
            background: 'transparent', border: 'none', padding: 0, fontFamily: TT.fontMono, fontSize: 12,
            fontWeight: 700, color: TT.orange, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? 'saving…' : '$ retry save'}
        </button>
      )}
    </div>
  );
};

export const ResultPanel = ({ result, save }) => {
  if (!result) return null;

  const { passed, score, passThreshold } = result;
  const feedback = result.llm?.feedback ?? [];
  const orderedFeedback = passed ? feedback : [...feedback].sort((a, b) => Number(a.ok) - Number(b.ok));
  const detChecks = result.deterministic?.checks ?? [];
  const orderedDet = passed ? detChecks : [...detChecks].sort((a, b) => Number(a.passed) - Number(b.passed));
  const hiddenResults = result.hiddenCase?.results ?? [];

  return (
    <div style={{ marginTop: 20, paddingTop: 20, borderTop: `1px solid ${TT.border}`, maxWidth: PROSE_MAX_WIDTH }}>
      {/* 1. Verdict strip */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
          <span style={{
            fontFamily: TT.fontMono, fontWeight: 900, fontSize: 20, letterSpacing: '0.03em',
            color: passed ? TT.green : TT.amber,
          }}>{passed ? 'PASSED' : 'NOT YET'}</span>
          <span style={{ fontFamily: TT.fontMono, fontSize: 13, color: TT.textDim }}>{score} · pass at {passThreshold}</span>
        </div>
        <p style={{ fontFamily: TT.fontSans, fontSize: 13.5, color: TT.text, lineHeight: 1.6, margin: 0 }}>
          {interpretiveSentence(passed, feedback, detChecks)}
        </p>
        {passed && <SaveNotice save={save} />}
      </div>

      {/* 2. Rubric feedback — expanded by default, both outcomes */}
      {orderedFeedback.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontFamily: TT.fontMono, fontSize: 10, color: TT.textDim, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
            rubric feedback
          </div>
          {orderedFeedback.map((f, i) => (
            <CriterionRow key={i} name={f.criterion} passed={f.ok} detail={f.text} />
          ))}
        </div>
      )}

      {/* 3. Deterministic checks — collapsed on pass, expanded on fail */}
      {orderedDet.length > 0 && (
        <CollapsibleSection title="deterministic checks" defaultOpen={!passed} resetKey={result}>
          {orderedDet.map((c, i) => (
            <CriterionRow key={i} name={c.name} passed={c.passed} detail={c.detail} />
          ))}
        </CollapsibleSection>
      )}

      {/* 4. Hidden cases — anonymous verdict cards, Transfer (or any rep
          with needs_hidden_cases) */}
      {hiddenResults.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontFamily: TT.fontMono, fontSize: 10, color: TT.textDim, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
            hidden cases ({hiddenResults.filter(r => r.passed).length}/{hiddenResults.length} passed)
          </div>
          {hiddenResults.map(r => (
            <HiddenCaseCard key={r.position} position={r.position} passed={r.passed} capability={r.capability} />
          ))}
        </div>
      )}

      <div style={{ marginTop: 20 }}>
        <span style={{
          fontFamily: TT.fontMono, fontSize: 12, fontWeight: 700,
          color: passed ? TT.green : TT.orange,
        }}>
          {passed ? '$ continue' : '$ revise'}
        </span>
      </div>
    </div>
  );
};

export default ResultPanel;
